/* ============================================================================
   LINFA — LIVING HERBARIUM · theme.js
   Vanilla JS only. No libraries, no CDN dependencies.
   1. Botanical SVG draw-in (stroke-dashoffset + IntersectionObserver)
   2. Section reveal on entry
   3. Drawers (mobile nav, cart)
   4. Quantity steppers + variant chips
   5. Newsletter / form niceties
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------ 1. botanical draw-in */
  function prepareBotanical(svg) {
    if (svg.dataset.linfaPrepared) return;
    svg.dataset.linfaPrepared = 'true';
    var shapes = svg.querySelectorAll('path, circle, line, polyline, ellipse');
    var total = 0;
    shapes.forEach(function (shape, i) {
      var len = 0;
      try {
        len = shape.getTotalLength ? shape.getTotalLength() : 0;
      } catch (e) {
        len = 0;
      }
      if (!len) len = 400;
      len = Math.ceil(len) + 2;
      shape.style.setProperty('--dash', len);
      shape.style.strokeDasharray = len;
      shape.style.strokeDashoffset = len;
      shape.style.transitionProperty = 'stroke-dashoffset';
      shape.style.transitionTimingFunction = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
      shape.style.transitionDuration = (0.9 + Math.min(len, 900) / 900) + 's';
      shape.style.transitionDelay = (i * 26) + 'ms';
      total = i;
    });
    svg.classList.add('is-drawable');
    return total;
  }

  function drawBotanical(svg) {
    if (svg.dataset.linfaDrawn) return;
    svg.dataset.linfaDrawn = 'true';
    var shapes = svg.querySelectorAll('path, circle, line, polyline, ellipse');
    shapes.forEach(function (shape) {
      shape.style.strokeDashoffset = '0';
    });
    svg.classList.add('is-drawn');
  }

  function initBotanicals() {
    var svgs = document.querySelectorAll('.botanical-svg');
    if (!svgs.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    svgs.forEach(prepareBotanical);

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            drawBotanical(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    svgs.forEach(function (svg) {
      io.observe(svg);
    });

    /* hero botanical starts drawing as soon as the page settles */
    var hero = document.querySelector('.hero .botanical-svg');
    if (hero) {
      window.requestAnimationFrame(function () {
        window.setTimeout(function () {
          drawBotanical(hero);
        }, 260);
      });
    }
  }

  /* --------------------------------------------------- 2. section reveal */
  function initReveal() {
    var items = document.querySelectorAll('[data-reveal]');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var group = entry.target.parentElement
            ? entry.target.parentElement.querySelectorAll(':scope > [data-reveal]')
            : [entry.target];
          var index = Array.prototype.indexOf.call(group, entry.target);
          entry.target.style.setProperty('--reveal-delay', Math.max(index, 0) * 90 + 'ms');
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------- 3. drawers */
  var lastFocus = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var focusable = drawer.querySelector('[data-drawer-close], a, button, input');
    if (focusable) focusable.focus();
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function initDrawers() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        event.preventDefault();
        openDrawer(document.getElementById(opener.getAttribute('data-drawer-open')));
        return;
      }
      var closer = event.target.closest('[data-drawer-close]');
      if (closer) {
        event.preventDefault();
        closeDrawer(closer.closest('.drawer'));
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = document.querySelector('.drawer[aria-hidden="false"]');
      if (open) closeDrawer(open);
    });
  }

  /* ------------------------------------------ 4. quantity + variant chips */
  function initQuantity() {
    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-qty]');
      if (!btn) return;
      event.preventDefault();
      var field = btn.parentElement.querySelector('input[name="quantity"], input.qty__input');
      if (!field) return;
      var value = parseInt(field.value, 10) || 1;
      value += btn.getAttribute('data-qty') === 'up' ? 1 : -1;
      field.value = Math.max(1, value);
      field.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function initVariants() {
    document.querySelectorAll('[data-variant-picker]').forEach(function (picker) {
      var select = picker.querySelector('select[name="id"]');
      picker.querySelectorAll('.chip[data-variant-id]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          picker.querySelectorAll('.chip[data-variant-id]').forEach(function (c) {
            c.classList.remove('is-active');
            c.setAttribute('aria-pressed', 'false');
          });
          chip.classList.add('is-active');
          chip.setAttribute('aria-pressed', 'true');
          if (select) {
            select.value = chip.getAttribute('data-variant-id');
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          var priceTarget = document.querySelector('[data-variant-price]');
          var price = chip.getAttribute('data-variant-price');
          if (priceTarget && price) priceTarget.textContent = price;
          var available = chip.getAttribute('data-variant-available') === 'true';
          var submit = document.querySelector('.buy__submit');
          if (submit) {
            submit.disabled = !available;
            submit.textContent = available
              ? submit.getAttribute('data-label-default') || 'Add to order'
              : submit.getAttribute('data-label-soldout') || 'Out of stock';
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------ 5. forms */
  function initForms() {
    document.querySelectorAll('form[data-demo-form]').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var note = form.parentElement.querySelector('[data-form-note]');
        if (note) note.textContent = 'Noted — your first dispatch arrives with Vol. I.';
        form.reset();
      });
    });
  }

  function init() {
    initBotanicals();
    initReveal();
    initDrawers();
    initQuantity();
    initVariants();
    initForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
